import React, {useEffect, useMemo, useState} from "react";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { gettingCreditHolidayRule } from "utils/CreditHolidayRule";
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CachedIcon from '@mui/icons-material/Cached';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AddCreditHolidayRule } from "components/dialog/credit_holiday_rule/AddCreditHolidayRule";
import { EditCreditHolidayRule } from "components/dialog/credit_holiday_rule/EditCreditHolidayRule"
import { DeleteCreditHolidayRule } from "components/dialog/credit_holiday_rule/DeleteCreditHolidayRule"
import { InfoCreditHolidayRule } from "components/dialog/credit_holiday_rule/InfoCreditHolidayRule"
import { useLocation, useNavigate } from 'react-router-dom';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

const ContextMenu = ({contextMenu, setContextMenu, selectedRow, reloading, rows}) => {
    const { t } = useTranslation()
    const location = useLocation();
    const navigate = useNavigate();
    const [openDelete, setOpenDelete] = useState(false)
    const basePath = buildSettingsBasePath('credit_holiday_rules');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'credit_holiday_rules'), [location.pathname]);
    const openAdd = modalRoute.action === 'add';
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null;
    const openInfo = modalRoute.action === 'detail' && modalRoute.id !== null;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeRouteModal = () => {
        navigate(basePath);
    };


    const options = [
        {
            name: t('settings.credit_holiday_rule.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('credit_holiday_rules', 'add'))
                setContextMenu(null)
                },
            color: '',
            selected: false,
            onlyOne: false,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.CreateCreditHolidayRule') ? true : false,
        },
        {
            name: t('settings.credit_holiday_rule.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('credit_holiday_rules', 'update', current.id))
                setContextMenu(null)
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.UpdateCreditHolidayRule') ? true : false,
        },
        {
            name: t('settings.credit_holiday_rule.table.context_menu.delete'),
            icon: <DeleteIcon color='error'/>,
            click: () => {
                setOpenDelete(true)
                setContextMenu(null)
            },
            color: 'error',
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: false,
            onlyOne: true,
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.DeleteCreditHolidayRule') ? true : false,
        },
        {
            name: t('settings.credit_holiday_rule.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1 ? true : false,
            click: () => {
                const current = selectedRow[0]
                if (!current?.id) return
                navigate(buildSettingsCrudPath('credit_holiday_rules', 'detail', current.id))
                setContextMenu(null)
            },
            color: '',
            permision: JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.CreditHolidayRulesById') ? true : false,
        },
        {
            name: t('settings.credit_holiday_rule.table.context_menu.reload'),
            icon: <CachedIcon/>,
            color: '',
            click: () => {
                reloading()
                setContextMenu(null)
            },
            permision: true,
        }
    ]


    return (
        <div>
            <Menu
                open={contextMenu ? true : false }
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined }
                sx={{boxShadow: 'none'}}
            >
                { options.filter(option => option.permision).map((option, index) =>
                    <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                        <ListItemIcon>
                            { option.icon }
                        </ListItemIcon>

                        <ListItemText primary={option.name} slotProps={{ primary: { color: option.color }}}/>
                    </MenuItem>
                )}
            </Menu>

            <AddCreditHolidayRule open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} reloading={reloading} rows={rows}/>
            <EditCreditHolidayRule open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} reloading={reloading} selectedRow={selectedRowsForRoute} rows={rows}/>
            <DeleteCreditHolidayRule open={openDelete} setOpenDelete={setOpenDelete} selectedRow={selectedRow} reloading={reloading} rows={rows}/>
            <InfoCreditHolidayRule open={openInfo} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} selectedRow={selectedRowsForRoute}/>

        </div>
    )
}

const CreditHolidayRuleTable = () => {
    const { t } = useTranslation()

    const [loading, setLoading] = useState(true)
    const [rows, setRows] = useState([])
    const [contextMenu, setContextMenu]   = useState(null)
    const [selectedRow, setSelectedRow]   = useState([])
    const [selected, setSelected] = useState([])

    const columns = [
        {
            field: 'name',
            headerName: t('settings.credit_holiday_rule.table.columns.name'),
            flex: 1,
        },
        {
            field: 'credit_type',
            headerName: t('settings.credit_holiday_rule.table.columns.credit_type'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                switch(params.value){
                    case 'fixed_quantity_of_hours':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.fixed_quantity_of_hours')
                    case 'fixed_salaries':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.fixed_salaries')
                    case 'hours_from_schedule':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.hours_from_schedule')
                    case 'percentage_of_salaries':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.percentage_of_salaries')
                    case 'average_shift_duration':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.average_shift_duration')
                    case 'average_salaries':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.average_salaries')
                    case 'average_shift_duration_with_free_time_rule':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.average_shift_duration_with_free_time_rule')
                    case 'average_salaries_with_free_time_rule':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.average_salaries_with_free_time_rule')
                    case 'percentage_of_salaries_utilizing_free_time_rule':
                        return t('settings.credit_holiday_rule.dialog.inputs.types_credits_selectable.percentage_of_salaries_utilizing_free_time_rule')
                    default:
                        return params.value
                }
            }

        },
        {
            field: 'amount_hours',
            headerName: t('settings.credit_holiday_rule.table.columns.amount_hours'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    params.value !== null ? params.value : "--:--"
                )
            }
        },
        {
            field: 'enable',
            headerName: t('settings.credit_holiday_rule.table.columns.enable'),
            flex: 1,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    params.value === true ? <CheckIcon style={{marginTop: '10px', marginBottom: '10px'}}/> : <CloseIcon style={{marginTop: '10px', marginBottom: '10px'}}/>
                )
            },
            sortable: false
        }

    ]

    const reloading = () => {
        if(localStorage.getItem('access_token') && JSON.parse(localStorage.getItem("permissions")).find(permision => permision === 'timekeeper.CreditHolidayRules')) {
            gettingCreditHolidayRule(setLoading, setRows)
        }else{
            setRows([])
            setLoading(false)
        }
    }

    useEffect(() => {
        reloading(setLoading, setRows)
    }, [])

    return (
        <>
            <div
                sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6} : null)
                }}
            >
                <DataGridPro
                    sx={{ height: 'calc(100vh - 140px)' }}
                    columns={columns}
                    rows={rows}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    headerFilters
                    localeText={
                        {
                            headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                            headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                            headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                            headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                            headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                            headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                            footerRowSelected: (count) =>
                                count > 1
                                ? t('general.table.footerFilters.footerRowSelected.>1', {count: count.toLocaleString()})
                                : t('general.table.footerFilters.footerRowSelected.=1', {count: count.toLocaleString()}),
                            footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                            MuiTablePagination: {
                                labelDisplayedRows: ({ from, to, count }) =>
                                    t('general.table.footerFilters.labelDisplayedRows', {from: from.toLocaleString(), to: to.toLocaleString(), count: count.toLocaleString()}),
                                labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),},

                        }
                    }
                    disableColumnMenu
                    loading={loading}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        let row = rows.filter((row) => newRowSelectionModel.includes(row.id))
                        setSelectedRow(newRowSelectionModel)
                        setSelected(row);
                    }}
                    rowSelectionModel={selectedRow}
                    pagination
                />

                <ContextMenu
                    contextMenu={contextMenu}
                    setContextMenu={setContextMenu}
                    selectedRow={selected}
                    reloading={reloading}
                    rows={rows}
                />

            </div>
        </>
    )
}

export {CreditHolidayRuleTable}